import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { generateCacheKey, withManualCache, DEFAULT_CACHE_TTL } from '@/utlility/cache-service';
const API_URL = process.env.NEXT_PUBLIC_API_URL;

// Fetch paginated blogs list
export const fetchBlogsData = createAsyncThunk(
  'blogs/fetchBlogsData',
  async ({payload}, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/blogs-data`;
      const cacheKey = generateCacheKey(url, 'POST', payload);
      return await withManualCache(cacheKey, async () => {
        const response = await axios.post(url, payload);
        return response.data;
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Fetch single blog details by slug or ID
export const fetchBlogDetails = createAsyncThunk(
  'blogs/fetchBlogDetails',
  async ({payload}, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/blog-data`;
      const cacheKey = generateCacheKey(url, 'POST', payload);
      return await withManualCache(cacheKey, async () => {
        const response = await axios.post(url, payload);
        return response.data;
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Fetch recent blogs for sidebar
export const fetchRecentBlogs = createAsyncThunk(
  'blogs/fetchRecentBlogs',
  async ({ payload }, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/blogs-data`;
      const cacheKey = generateCacheKey(url, 'POST', payload);
      return await withManualCache(cacheKey, async () => {
        const response = await axios.post(url, payload);
        return response.data;
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const fetchTags = createAsyncThunk(
  'blogs/fetchTags',
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/tags`;
      const cacheKey = generateCacheKey(url, 'POST', payload);
      return await withManualCache(cacheKey, async () => {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          next: { revalidate: DEFAULT_CACHE_TTL },
        });
        return await response.json();
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

const blogsSlice = createSlice({
  name: 'blogs',
  initialState: {
    blogsData: null,
    recentBlogs: null,
    blogDetails: null,
    tags: [],
    status: 'idle',
    recentStatus: 'idle',
    detailsStatus: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Unified blogs data fetching (list/pagination/filters)
      .addCase(fetchBlogsData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBlogsData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.blogsData = action.payload.data;
      })
      .addCase(fetchBlogsData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchBlogDetails.pending, (state) => {
        state.detailsStatus = 'loading';
      })
      .addCase(fetchBlogDetails.fulfilled, (state, action) => {
        state.detailsStatus = 'succeeded';
        state.blogDetails = action.payload.data;
      })
      .addCase(fetchBlogDetails.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.error = action.payload;
      })
 
      .addCase(fetchTags.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.meta.arg.page === 1) {
          state.tags = action.payload;
        } else {
          state.tags = {
            ...action.payload,
            data: {
              ...action.payload.data,
              data: [...(state.tags?.data?.data || []), ...action.payload.data.data]
            }
          };
        }
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchRecentBlogs.pending, (state) => {
        state.recentStatus = 'loading';
      })
      .addCase(fetchRecentBlogs.fulfilled, (state, action) => {
        state.recentStatus = 'succeeded';
        state.recentBlogs = action.payload.data;
      })
      .addCase(fetchRecentBlogs.rejected, (state, action) => {
        state.recentStatus = 'failed';
        state.error = action.payload;
      })
  },
});

export default blogsSlice.reducer;
